import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AccessTime, Info, LocationOn, Search } from "@mui/icons-material";
import { Box, Button, Chip, Link, Tooltip, Typography } from "@mui/material";
import { getBulletinMyAccess } from "@rubi/api-generated";
import { HighlightText, type MediaGalleryHandle } from "@rubi/ui";
import { Trans, useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useBulletinTypeLabel, useCrimeLabel } from "@/api/loadAlerts";
import { EmailButton, PhoneButton } from "@/common";
import CompareMediaDisplay from "@/common/CompareMediaDisplay";
import {
  formatDateLong,
  formatIncidentDate,
  formatIncidentDateTime,
} from "@/common/utils";
import styles from "@/components/alerts/add/DisplayAlert/DisplayAlert.module.scss";
import DisplayAttachedPDF from "@/components/alerts/add/DisplayAlert/DisplayAttachedPdf";
import DownloadReportPDF from "@/components/alerts/add/DisplayAlert/DownloadAlertPDF/DownloadReportPDF";
import { PublishedResolution } from "@/components/alerts/add/DisplayAlert/ResolveBulletin";
import {
  approvedByText,
  creatorText,
} from "@/components/alerts/add/DisplayAlert/utils";
import { bulletinEditPath } from "@/components/alerts/add-v2/routePaths";
import AlertMediaSlider from "@/components/alerts/AlertMediaSlider";
import { BulletinSectionHeading } from "@/components/alerts/BulletinSectionHeading";
import { LOCATION_CLASSIFICATION_REFERENCE } from "@/components/alerts/constants";
import { SingleAlertContext } from "@/components/alerts/context";
import { BulletinEntityList } from "@/components/alerts/entities/BulletinEntityList";
import {
  getAddressClassification,
  getAddressDescription,
} from "@/components/alerts/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { usePoliceServiceNameImage } from "@/hooks/useCommunities";
import {
  IBulletinResolution,
  IEvidenceFile,
  IPdfFile,
  MediaBlob,
  PlaceType,
} from "@/types/typesAlerts";

interface DisplayAlertProps {
  mode: "add" | "view" | "parade";
  issuing_police_service: string;
  associatedCrimes: string[];
  occurrenceNumbers: string[];
  issuedTime: string | null;
  description: string | null;
  pdf: IPdfFile | Record<string, never>;
  evidences: { [key: string]: IEvidenceFile };
  view_token?: any; //TODO Create IView_token
}

export const DisplayAlert = ({
  mode,
  issuing_police_service,
  associatedCrimes = [],
  occurrenceNumbers = [],
  issuedTime,
  pdf,
  evidences = {},
  view_token,
}: DisplayAlertProps) => {
  const policeServiceNameImage = usePoliceServiceNameImage();
  const { alertProperties } = useContext(SingleAlertContext);
  const { currentUser, currentUserGroups } = useContext(AuthStateContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation(["bulletin", "bulletin/classification"]);
  const locale = i18n.language;
  const crimeLabel = useCrimeLabel();
  const bulletinTypeLabel = useBulletinTypeLabel();

  const {
    type,
    approvedBy,
    approvedByBadge,
    operationalArea,
    incidentTimes,
    contacts,
    bulletinId: alertId,
    entities,
    creator,
    resolutions,
    rawAddresses,
  } = alertProperties;
  const publishedResolutions = resolutions.filter(
    (resolution: IBulletinResolution) => resolution.published,
  );

  const [lang, setLang] = useState(
    alertProperties.originalLanguage === "fr" && i18n.language === "fr"
      ? "fr"
      : "en",
  );
  const creatorToUse = creator ? creator : currentUser;

  const attachmentsArray = useMemo(
    () =>
      Object.values(evidences).sort(
        (a: any, b: any) => a?.sort_ordinal - b?.sort_ordinal,
      ),
    [evidences],
  );

  const [selectedIndex, setSelectedIndex] = useState(-1);
  /** So an entity's photo letters can put that photo on the gallery's hero. */
  const galleryRef = useRef<MediaGalleryHandle>(null);
  const [isSlideShowActive, setIsSlideShowActive] = useState(false);

  /**
   * The breakpoint is the panel, not the viewport.
   *
   * A bulletin is read in a drawer the reader drags to any width, and next to a second bulletin
   * when two are compared, so `useMediaQuery` would call a 500px panel on a large monitor a desktop
   * and lay it out as one. Measured here and answered by the crest, the title and the tooltip; the
   * gallery and the entity rows measure themselves the same way.
   */
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setPanelWidth(Math.round(entry.contentRect.width)),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const isSmall = (panelWidth ?? window.innerWidth) < 600;

  const navigate = useNavigate();
  const location = useLocation();
  const isDraftsRoute = location.pathname.includes("/drafts");
  const isEditRoute = location.pathname.includes("/edit");
  const isAlertSingleView = location.pathname.includes("/single");
  const isSelectedAlert =
    searchParams.get("bulletinId") === alertId?.toString();
  const isCompareAlert =
    searchParams.get("compareBulletinId") === alertId?.toString();
  const searchText = searchParams.get("searchText");

  useEffect(() => {
    if (selectedIndex === -1) {
      setIsSlideShowActive(false);
      return;
    }
    setIsSlideShowActive(true);
  }, [selectedIndex]);

  /**
   * The bulletin's media as blobs, for the generated PDF -- its only consumer.
   *
   * Deliberately not run on mount. Every attachment here is bytes the page has
   * already fetched (or will fetch) through the gallery's own `<img>`/`<video>`
   * tags, and holding a second, decoded copy of each original in memory is what
   * made this page unusable on phones. The PDF button pays for it on click.
   */
  const loadMediaBlobs = async (): Promise<MediaBlob[]> => {
    const blobs = await Promise.all(
      attachmentsArray.map(async (attachment: IEvidenceFile) => {
        try {
          const response = await fetch(getDownloadUrl(attachment), {
            credentials: "include",
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch ${attachment.filename}`);
          }
          return {
            blob: await response.blob(),
            filename: attachment.filename,
            media_type: attachment.content_type.includes("video")
              ? "video"
              : "image",
          } as MediaBlob;
        } catch (error) {
          // One unavailable attachment should cost the reader that photo, not
          // the whole PDF.
          console.error("Error fetching attachment for PDF", error);
          return null;
        }
      }),
    );
    return blobs.filter((blob): blob is MediaBlob => blob !== null);
  };

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchParams.get("bulletinId"), searchParams.get("compareBulletinId")]);

  const getDownloadUrl = (attachment: IEvidenceFile) => {
    const url = new URL(attachment?.download_url ?? "");
    return url.toString();
  };

  const getMediaIndexUrl = (index: number) => {
    if (index === -1) return "";
    if (!attachmentsArray) return "";
    const item = attachmentsArray[index];

    return getDownloadUrl(item);
  };

  let policeLogo;
  if (policeServiceNameImage[issuing_police_service])
    policeLogo = policeServiceNameImage[issuing_police_service];
  else policeLogo = policeServiceNameImage["DRDC"];

  const RubiconLogo = () => (
    <div className={styles.logo}>
      <b style={{ fontSize: 12, color: "grey" }}>POWERED BY</b>
      <img
        src="/Rubicon Logo.png"
        alt="Rubicon logo"
        style={{ height: "28px", marginTop: "-8px" }}
        onClick={() => window.open("https://tryRubicon.com", "_blank")}
      />
    </div>
  );

  const [openTooltip, setOpenTooltip] = useState(false);
  const handleTooltipToggle = () => {
    setOpenTooltip(!openTooltip);
  };

  /**
   * The addresses worth rendering, with the index they had in `rawAddresses` — the map view numbers
   * its pins off that, so filtering the blanks out cannot be allowed to renumber them.
   */
  const addresses = (rawAddresses ?? [])
    .map((address: PlaceType, index: number) => ({ address, index }))
    .filter(({ address }) => address);

  /** What kind of address this is. An address with no classification of its own is the incident's. */
  const namedClassification = (address: PlaceType) =>
    getAddressClassification(address) ||
    LOCATION_CLASSIFICATION_REFERENCE["Incident Address"];

  /**
   * The column the kind of a location or a time is read in.
   *
   * Wide enough for "Safe House Address" and "Not applicable", so the addresses and the times under
   * them start in one column rather than each wherever its own label ended.
   */
  /** The glyph each row opens with: a place or a time, told apart before either is read. */
  const rowIcon = {
    fontSize: 16,
    color: "text.secondary",
    flexShrink: 0,
    alignSelf: "flex-start",
    mt: "3px",
  } as const;

  const fieldLabel = {
    flex: "0 0 140px",
    color: "text.secondary",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  } as const;

  /** Content sits past its heading, in the same column as the entity rows' avatars. */
  const indentedBlock = {
    pl: 2,
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  };

  /** The two footer cards. A bordered panel, not a `Paper`: the bulletin is already on one. */
  /**
   * Sections are spacing, not cards.
   *
   * Entities, contacts and provenance each sat in a bordered, rounded box. Three of them down a
   * column read as three unrelated documents, and the border bought nothing the headings had not
   * already said; what separates them now is their heading and the space above it.
   */
  const sectionStyle = { mt: 2 } as const;

  const iconStyle = {
    color: "darkblue",
    cursor: "pointer",
    marginLeft: 1,
    fontSize: isSmall ? 20 : 14,
  };

  const linkToMapView = (addressText: string, addressIndex: number) => {
    if (mode === "view") {
      window.open(
        `https://maps.google.com/maps/place/${encodeURIComponent(addressText)}`,
        "_blank",
      );
      return;
    }

    if (isAlertSingleView) {
      navigate(
        `/rubialert/alerts?bulletinId=${alertId}&view=map&addressIndex=${addressIndex}`,
      );
      return;
    }

    setSearchParams((searchParams) => {
      searchParams.set("bulletinId", alertId ? alertId.toString() : "0");
      searchParams.set("view", "map");
      if (isSelectedAlert) {
        searchParams.set("selectedAddressIndex", addressIndex.toString());
      } else if (isCompareAlert) {
        searchParams.set("compareAddressIndex", addressIndex.toString());
      }
      return searchParams;
    });
  };

  const languageToggleForBulletin = () => {
    // Only show language toggle if bulletin is in French
    if (alertProperties.originalLanguage !== "fr") return null;
    return (
      <div className="mb-2 text-right fs-sm">
        {lang === "fr" ? (
          <div>
            View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("en")}
            >
              English
            </a>{" "}
            Bulletin
          </div>
        ) : (
          <div>
            This Bulletin is originally in {alertProperties.originalLanguage},
            View{" "}
            <a
              style={{
                color: "blue",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setLang("fr")}
            >
              French
            </a>{" "}
            Bulletin
          </div>
        )}
      </div>
    );
  };

  const incidentTimeText = (incidentTime: {
    start_date: string;
    end_date: string;
    time_type: string;
  }) => {
    // `formatIncidentDateTime`, not `formatDateLong`: short month and 24-hour, which is how a police
    // service writes a time to be matched against an occurrence. *Issued on* keeps the long form —
    // the two dates on this page are read for different things and the design writes them
    // differently.
    if (incidentTime.time_type === "range") {
      return `${formatIncidentDateTime(incidentTime.start_date, locale)} - ${formatIncidentDateTime(incidentTime.end_date, locale)}`;
    } else if (incidentTime.time_type === "exact") {
      return formatIncidentDateTime(incidentTime.start_date, locale);
    } else if (incidentTime.time_type === "time_unknown") {
      return formatIncidentDate(incidentTime.start_date, locale);
    }
    // "Unknown" and "Not applicable" say everything they have to say in their type.
    return "";
  };

  return (
    <>
      <div
        ref={panelRef}
        className="mt-md-4 px-md-4 w-90 mx-auto"
        style={isSlideShowActive ? { height: "80vh", overflow: "hidden" } : {}}
      >
        <div className={styles.alert_preview_container + " mb-2 "}>
          {mode === "view" && (
            <div className={styles.viewOnlyHeader}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Search />}
                onClick={async () => {
                  // From a shared bulletin, open the Bulletin Board and, if the
                  // user has access, open the shared bulletin in the side panel
                  // (pinned to front). Otherwise just open the board as usual.
                  let canView = false;
                  try {
                    const res = await getBulletinMyAccess({
                      path: { bulletin_id: Number(alertId) },
                    });
                    canView = res.data?.can_view ?? false;
                  } catch {
                    canView = false;
                  }

                  if (canView) {
                    navigate(
                      `/rubialert/alerts?bulletinId=${alertId}&pinToFront=true`,
                    );
                  } else {
                    navigate("/rubialert/alerts");
                  }
                }}
                sx={{ margin: "0 8px 8px 8px" }}
              >
                See All Bulletins
              </Button>
              <RubiconLogo />
            </div>
          )}

          {alertProperties.isDraft && isDraftsRoute && !isEditRoute && (
            <div style={{ textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => navigate(bulletinEditPath(alertId ?? ""))}
                sx={{ margin: "0 8px 8px 8px" }}
              >
                {t("bulletin:draft_click_edit")}
              </Button>
            </div>
          )}

          {publishedResolutions
            ?.sort(
              (a, b) =>
                new Date(b.time_updated).getTime() -
                new Date(a.time_updated).getTime(),
            )
            .map((resolution) => (
              <div
                key={resolution.id}
                className={styles.publishedResolutions}
                style={{ margin: "16px auto" }}
              >
                <PublishedResolution resolution={resolution} />
              </div>
            ))}

          <div className={styles.header_container}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  color: "darkblue",
                  textAlign: "center",
                  ...theme.applyStyles("dark", {
                    color: theme.palette.primary.light,
                  }),
                })}
              >
                ***{" "}
                {t(`bulletin/classification:${alertProperties.classification}`)}{" "}
                ***
              </Typography>
              <Tooltip
                title={
                  <Fragment>
                    <span>
                      <Trans
                        t={t}
                        i18nKey={`bulletin/classification:${alertProperties.classification}_description`}
                        components={{ b: <b /> }}
                      />
                    </span>{" "}
                    <Link
                      href="https://www.tbs-sct.canada.ca/pol/doc-eng.aspx?id=32614"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("bulletin:read_more_here")}
                    </Link>
                  </Fragment>
                }
                placement={isSmall ? "bottom" : "right"}
                onClose={() => setOpenTooltip(false)}
                open={openTooltip}
                arrow
              >
                <Info sx={iconStyle} onClick={handleTooltipToggle} />
              </Tooltip>
            </div>

            <div className={styles.alert_heading_inner1}>
              <div>
                <img
                  src={policeLogo}
                  className={styles.police_logo}
                  // Halved where the panel is narrow: at full size the crest takes the width the
                  // title needs to stay on two lines.
                  style={{
                    maxWidth: isSmall ? 40 : 80,
                    minWidth: isSmall ? 30 : 60,
                  }}
                  alt="Police logo"
                />
                {operationalArea && (
                  <Typography variant="subtitle1" align="center">
                    {operationalArea}
                  </Typography>
                )}
              </div>

              <div className={styles.type_crimes_container}>
                <Typography
                  align="center"
                  variant={isSmall ? "h6" : "h4"}
                  style={{ textTransform: "uppercase" }}
                >
                  <b>{bulletinTypeLabel(type ?? "") || <Skeleton />}</b>
                </Typography>

                <div className={styles.crimes_container}>
                  {associatedCrimes.map((crime, index) => (
                    <Chip
                      key={index}
                      variant="outlined"
                      label={crimeLabel(crime)}
                    />
                  ))}
                </div>
              </div>
              <div
                style={{
                  maxWidth: isSmall ? "40px" : "80px",
                  minWidth: isSmall ? "0px" : "60px",
                }}
              ></div>
            </div>
          </div>
          <div
            className="px-md-4 py-1 my-1 mx-auto"
            style={{
              maxWidth: "var(--page-max-width)",
            }}
          >
            <AlertMediaSlider
              ref={galleryRef}
              medias={attachmentsArray}
              entities={entities}
              handleSlideClick={(index) => setSelectedIndex(index)}
            />
          </div>

          <div
            className={styles.bulletin_container + " mx-2 mx-md-0 mx-auto px-3"}
          >
            {/* Language Toggle for bulletin only - if bulletin is in French, show toggle */}
            {languageToggleForBulletin()}
            {/*
              The tag block that used to sit here is gone: nothing writes tags any more, and the 390
              that existed were migrated into entities. The media slider has followed — it captions
              a photo with the entity it is linked to — so the generated PDF is the last reader of
              `tagDict` left.

              Entities are not translated — there is no `description_fr` on an entity the way there
              was on a tag, so the language toggle above does not reach them.
            */}
            {/*
              Entities, location and time in one card, because they are read together: they are the
              facts an officer scans to decide whether this bulletin is about the thing in front of
              them. The narrative below is prose and sits on the page itself.

              Each block's content is indented past its heading, so the entity avatars, the address
              and the time all start in the same column.
            */}
            <Box sx={sectionStyle}>
              <BulletinEntityList
                entities={entities}
                medias={attachmentsArray}
                onPhotoClick={(index) => galleryRef.current?.show(index)}
                searchText={searchText}
              />

              {addresses.length > 0 && (
                <>
                  <BulletinSectionHeading>
                    {t("bulletin:section_locations")}
                  </BulletinSectionHeading>
                  <Box sx={indentedBlock}>
                    {addresses.map(({ address, index }) => {
                      const isMapView = searchParams.get("view") === "map";
                      const classification = namedClassification(address);
                      // The unit is free text and usually names itself — "Unit 12", "Suite 8" — so
                      // it goes in front of the address rather than getting a label of its own. Only
                      // the address is handed to the map: a unit number does not help it geocode.
                      const line = [
                        address?.unit,
                        getAddressDescription(address),
                      ]
                        .filter((part) => part && String(part).trim())
                        .join(", ");
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 1,
                          }}
                        >
                          <LocationOn sx={rowIcon} />
                          {/* Which address this is — a suspect's home, a school, the incident's. */}
                          <Typography variant="caption" sx={fieldLabel}>
                            {classification}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.alertAddressLine}
                            sx={{ minWidth: 0 }}
                          >
                            {isMapView && (
                              <span
                                className={`${styles.alertAddressPin} ${isSelectedAlert ? styles.selectedAlertAddressPin : styles.compareAlertAddressPin}`}
                              >
                                {index + 1}
                              </span>
                            )}
                            {/*
                              Still opens the map view, and still only once the bulletin is
                              published — a draft has no pin to jump to. The trailing link icon is
                              gone with the bold: the hover state is the affordance the design
                              leaves it.
                            */}
                            {!alertProperties.isDraft ? (
                              <a
                                className={styles.alertAddressHover}
                                // Underlined where it stands rather than on hover: the design reads
                                // an address as a link to the map, not as text that turns into one.
                                style={{ textDecoration: "underline" }}
                                onClick={() =>
                                  linkToMapView(
                                    getAddressDescription(address),
                                    index,
                                  )
                                }
                              >
                                {line}
                              </a>
                            ) : (
                              line
                            )}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {incidentTimes.length > 0 && (
                <>
                  <BulletinSectionHeading>
                    {t("bulletin:section_times")}
                  </BulletinSectionHeading>
                  {/*
                    The per-row "Incident Time 1:" label is gone: the heading says what these are,
                    and the number it carried only ever meant "the second one down".
                  */}
                  <Box sx={indentedBlock}>
                    {incidentTimes.map((time, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                        }}
                      >
                        {/*
                          What kind of time this is, not only what it says: "Friday, Sep 18" alone
                          does not tell the reader whether the officer knew the hour.
                        */}
                        <AccessTime sx={rowIcon} />
                        <Typography variant="caption" sx={fieldLabel}>
                          {t(`bulletin:time_type_${time.time_type}`)}
                        </Typography>
                        <Typography variant="body2" sx={{ minWidth: 0 }}>
                          {incidentTimeText(time)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            <BulletinSectionHeading>
              {t("bulletin:section_narrative")}
            </BulletinSectionHeading>
            <Typography
              variant="body1"
              style={{ whiteSpace: "pre-line" }}
              paragraph
            >
              <HighlightText
                searchText={searchText}
                text={
                  lang === "fr"
                    ? alertProperties.descriptionFr
                    : alertProperties.description
                }
              />
            </Typography>
            {/*
              Two cards rather than a centred block of lines. What the officer is looking at here is
              *who to call*, and everything about the old arrangement worked against reading that:
              the name, badge, email and phone were one wrapping line, and the issuing service sat
              once at the bottom, centred, so nothing said which contact it belonged to.
            */}
            <Box sx={sectionStyle}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("bulletin:contact_info_header")}
              </Typography>

              {contacts
                .filter((contact) => contact.name)
                .map((contact, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body1" fontWeight={700}>
                      {[contact.name, contact.badge_number]
                        .filter((part) => part)
                        .join(" - ")}
                    </Typography>
                    {/*
                      Still the shared `PhoneButton` / `EmailButton`, so a tap dials and a click
                      opens a draft. They carry an icon the mock does not show; dropping it means
                      editing `@rubi/ui`, which the share dialog and the public board also render.
                    */}
                    {contact.phone_number !== "" && (
                      <div>
                        <PhoneButton
                          phoneNumber={contact.phone_number}
                          extension={contact.extension}
                        />
                      </div>
                    )}
                    {contact.email !== "" && (
                      <div>
                        <EmailButton
                          email={contact.email}
                          subject={`Inquiry about ${bulletinTypeLabel(type ?? "")} alert - ${occurrenceNumbers.join()}`}
                        />
                      </div>
                    )}
                    {/* Under each contact, not once at the foot of the bulletin. */}
                    <Typography variant="body1">
                      {issuing_police_service}
                    </Typography>
                  </Box>
                ))}

              <Typography variant="body1">
                {t("bulletin:reference_incident_number")}{" "}
                {occurrenceNumbers.join(", ")}
              </Typography>
            </Box>

            {/*
              Provenance, in its own card and in the same grey-label/dark-value shape as the rest:
              who wrote it, who approved it, when it went out. It was three centred `caption` lines,
              one of which was a bare " | Bulletin ID - 12" that read as a fragment on its own.
            */}
            <Box sx={sectionStyle}>
              {creatorToUse && creatorText(creatorToUse) && (
                <Typography variant="body2">
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {t("bulletin:prepared_by")}
                  </Box>{" "}
                  {creatorText(creatorToUse)}
                </Typography>
              )}
              {(approvedBy || approvedByBadge) && (
                <Typography variant="body2">
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {t("bulletin:approved_by")}
                  </Box>{" "}
                  {approvedByText(approvedBy, approvedByBadge)}
                </Typography>
              )}
              <Typography variant="body2">
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {t("bulletin:issued_on")}
                </Box>{" "}
                {issuedTime ? (
                  formatDateLong(issuedTime, locale)
                ) : (
                  <>
                    <i>(Pending)</i> {formatDateLong(new Date(), locale)}
                  </>
                )}
                {/* Not `!== 0`: that is true for `undefined` too, and the add-v2 preview
                    passes no id at all — which rendered "Bulletin ID - undefined". */}
                {!!alertId && ` | Bulletin ID - ${alertId}`}
              </Typography>
            </Box>
          </div>
          {pdf && Object.keys(pdf).length > 0 && (
            <div className={styles.attached_pdf_container}>
              <DisplayAttachedPDF
                pdfFile={pdf}
                mode={mode}
                view_token={view_token}
              />
            </div>
          )}
          <div className={styles.generated_pdf_container}>
            <DownloadReportPDF
              policeLogo={policeLogo}
              loadMediaBlobs={loadMediaBlobs}
              issuedTime={
                issuedTime
                  ? formatDateLong(issuedTime, locale)
                  : formatDateLong(new Date(), locale)
              }
            />
          </div>
          {isSlideShowActive && (
            <CompareMediaDisplay
              isSlideShowActive={isSlideShowActive}
              setIsSlideShowActive={setIsSlideShowActive}
              mediaSrc={getMediaIndexUrl(selectedIndex)}
              mediaType={
                attachmentsArray[selectedIndex]?.content_type.includes("video")
                  ? "video"
                  : "image"
              }
              selectedIndex={selectedIndex}
              setSelectedIndex={setSelectedIndex}
              slideshowLength={attachmentsArray.length}
            />
          )}
        </div>
      </div>
    </>
  );
};
